import {
  apiDownload,
  apiGet,
  apiPost,
  buildApiRoute,
  getPaged,
} from "./entityApiShared.js";

import {
  createIdempotencyKey,
} from "./api.service.js";

import {
  cmtsDashboardReportApiRoutes,
} from "../core/api/cmtsDashboardReportApiRoutes.js";

import type {
  EntityCreateRequest,
  EntityRecord,
  PageQuery,
  PagedResult,
} from "../types/api.types.js";

export type ReportCatalogItemDto = EntityRecord;
export type ReportPreviewRequest = EntityCreateRequest;
export type ReportPreviewResultDto = EntityRecord;
export type ReportExportRequest = EntityCreateRequest;
export type ReportExportJobDto = EntityRecord;

export type ReportBlobDownload = {
  readonly blob: Blob;
  readonly filename: string;
};

const SESSION_STORAGE_KEY = "swot.cmts.session.v1";

function getTenantId(): string | undefined {
  try {
    const raw = globalThis.localStorage?.getItem(
      SESSION_STORAGE_KEY,
    );

    if (!raw) {
      return undefined;
    }

    const session = JSON.parse(raw) as {
      readonly tenantId?: unknown;
    };

    const tenantId =
      typeof session.tenantId === "string"
        ? session.tenantId.trim()
        : "";

    return tenantId || undefined;
  } catch {
    return undefined;
  }
}

function requireTenantId(): string {
  const tenantId = getTenantId();

  if (!tenantId) {
    throw new Error(
      "Authentication is required. Sign in again and select a tenant.",
    );
  }

  return tenantId;
}

function requireExportId(exportId: string): string {
  const normalized = exportId.trim();

  if (!normalized) {
    throw new Error("Report export ID is required.");
  }

  return normalized;
}

function normalizeReportCode(
  payload: ReportExportRequest | ReportPreviewRequest,
  fallback = "",
): string {
  return String(
    payload.reportCode ??
    payload.code ??
    fallback,
  ).trim();
}

function normalizeExportFormat(
  payload: ReportExportRequest | ReportPreviewRequest,
  fallback = "",
): string {
  return String(
    payload.exportFormat ??
    payload.format ??
    fallback,
  )
    .trim()
    .toUpperCase();
}

function reportPayload(
  reportCode: string,
  payload:
    | ReportExportRequest
    | ReportPreviewRequest,
): EntityCreateRequest {
  const tenantId = requireTenantId();
  const normalizedReportCode = reportCode.trim();
  const format = normalizeExportFormat(payload);

  return {
    ...payload,
    tenantId,
    reportCode: normalizedReportCode,
    ...(format
      ? {
          format,
          exportFormat: format,
        }
      : {}),
  };
}

function extensionFromFormat(format: string): string {
  switch (format.trim().toUpperCase()) {
    case "CSV":
      return "csv";

    case "XLS":
    case "XLSX":
    case "EXCEL":
      return "xlsx";

    case "JSON":
      return "json";

    case "PDF":
      return "pdf";

    default:
      return "bin";
  }
}

export function safeReportFilename(
  input: string | undefined,
  fallback = "cmts-report-export.bin",
): string {
  const cleaned = (input ?? fallback)
    .replace(/[\\/:*?"<>|\u0000-\u001F]/g, "-")
    .replace(/\s+/g, " ")
    .trim();

  return cleaned || fallback;
}

export function filenameFromContentDisposition(
  header: string | null | undefined,
  fallback = "cmts-report-export.bin",
): string {
  if (!header) {
    return fallback;
  }

  const utfMatch =
    /filename\*=UTF-8''([^;]+)/i.exec(header);

  const utfFilename = utfMatch?.[1];

  if (utfFilename) {
    try {
      return safeReportFilename(
        decodeURIComponent(utfFilename),
        fallback,
      );
    } catch {
      return safeReportFilename(
        utfFilename,
        fallback,
      );
    }
  }

  const plainMatch =
    /filename="?([^";]+)"?/i.exec(header);

  const plainFilename = plainMatch?.[1];

  return safeReportFilename(
    plainFilename,
    fallback,
  );
}

export function downloadBlobInBrowser(
  blob: Blob,
  filename: string,
): void {
  const url = URL.createObjectURL(blob);

  try {
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = safeReportFilename(filename);
    anchor.rel = "noopener";

    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    globalThis.setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 0);
  }
}

export const reportApi = {
  /*
   * Report catalog
   */

  getReportCatalog: (
    query?: PageQuery,
    signal?: AbortSignal,
  ): Promise<
    PagedResult<ReportCatalogItemDto>
  > => {
    const tenantId = getTenantId();

    return getPaged<ReportCatalogItemDto>(
      cmtsDashboardReportApiRoutes.reports.catalog,
      {
        ...query,
        ...(tenantId ? { tenantId } : {}),
      } as PageQuery,
      signal,
    );
  },

  catalog: (
    query?: PageQuery,
    signal?: AbortSignal,
  ): Promise<
    PagedResult<ReportCatalogItemDto>
  > => {
    const tenantId = getTenantId();

    return getPaged<ReportCatalogItemDto>(
      cmtsDashboardReportApiRoutes.reports.catalog,
      {
        ...query,
        ...(tenantId ? { tenantId } : {}),
      } as PageQuery,
      signal,
    );
  },

  /*
   * Report schema and preview
   */

  getReportSchema: (
    reportCode: string,
    signal?: AbortSignal,
  ): Promise<EntityRecord> => {
    const normalizedReportCode =
      reportCode.trim();

    if (!normalizedReportCode) {
      throw new Error(
        "Report code is required.",
      );
    }

    return apiGet<EntityRecord>(
      buildApiRoute(
        cmtsDashboardReportApiRoutes.reports.schema,
        {
          reportCode: normalizedReportCode,
        },
      ),
      { signal },
    );
  },

  previewReport: (
    reportCode: string,
    payload: ReportPreviewRequest,
    signal?: AbortSignal,
  ): Promise<ReportPreviewResultDto> => {
    const normalizedReportCode =
      reportCode.trim();

    if (!normalizedReportCode) {
      throw new Error(
        "Report code is required.",
      );
    }

    return apiPost<ReportPreviewResultDto>(
      buildApiRoute(
        cmtsDashboardReportApiRoutes.reports.preview,
        {
          reportCode: normalizedReportCode,
        },
      ),
      reportPayload(
        normalizedReportCode,
        payload,
      ),
      { signal },
    );
  },

  preview: (
    payload: ReportPreviewRequest,
    idempotencyKey?: string,
  ): Promise<ReportPreviewResultDto> => {
    const reportCode = normalizeReportCode(
      payload,
      "cmts-report",
    );

    return apiPost<ReportPreviewResultDto>(
      buildApiRoute(
        cmtsDashboardReportApiRoutes.reports
          .legacyPreview,
      ),
      reportPayload(reportCode, payload),
      {
        idempotencyKey,
      },
    );
  },

  /*
   * Typed/canonical report generation
   */

  requestReportExport: (
    reportCode: string,
    payload: ReportExportRequest,
    signal?: AbortSignal,
    idempotencyKey = createIdempotencyKey(
      "cmts-report-export",
    ),
  ): Promise<ReportExportJobDto> => {
    const normalizedReportCode =
      reportCode.trim();

    if (!normalizedReportCode) {
      throw new Error(
        "Report code is required.",
      );
    }

    return apiPost<ReportExportJobDto>(
      buildApiRoute(
        cmtsDashboardReportApiRoutes.reports.export,
        {
          reportCode: normalizedReportCode,
        },
      ),
      reportPayload(
        normalizedReportCode,
        payload,
      ),
      {
        signal,
        idempotencyKey,
      },
    );
  },

  getReportExportStatus: (
    exportId: string,
    signal?: AbortSignal,
  ): Promise<ReportExportJobDto> => {
    const normalizedExportId =
      requireExportId(exportId);

    return apiGet<ReportExportJobDto>(
      buildApiRoute(
        cmtsDashboardReportApiRoutes.reports
          .exportStatus,
        {
          exportId: normalizedExportId,
        },
      ),
      { signal },
    );
  },

  createExport: (
    payload: ReportExportRequest,
    idempotencyKey?: string,
  ): Promise<ReportExportJobDto> => {
    const reportCode = normalizeReportCode(
      payload,
      "cmts-report",
    );

    return apiPost<ReportExportJobDto>(
      buildApiRoute(
        cmtsDashboardReportApiRoutes.reports.export,
        {
          reportCode,
        },
      ),
      reportPayload(reportCode, payload),
      {
        idempotencyKey,
      },
    );
  },

  /*
   * Report Export History controller
   *
   * Backend base route:
   * /api/cmts/report-exports
   */

  listReportExports: (
    signal?: AbortSignal,
  ): Promise<
    readonly ReportExportJobDto[]
  > => {
    const tenantId = requireTenantId();

    return apiGet<
      readonly ReportExportJobDto[]
    >(
      "/report-exports",
      {
        signal,
        query: {
          tenantId,
        },
      },
    );
  },

  getReportExport: (
    exportId: string,
    signal?: AbortSignal,
  ): Promise<ReportExportJobDto> => {
    const tenantId = requireTenantId();
    const normalizedExportId =
      requireExportId(exportId);

    return apiGet<ReportExportJobDto>(
      `/report-exports/${encodeURIComponent(
        normalizedExportId,
      )}`,
      {
        signal,
        query: {
          tenantId,
        },
      },
    );
  },

createReportExport: (
  payload: ReportExportRequest,
  signal?: AbortSignal,
): Promise<ReportExportJobDto> => {
  const tenantId = requireTenantId();

  const reportCode = String(
    payload.reportCode ??
    payload.code ??
    "",
  ).trim();

  const format = String(
    payload.format ??
    payload.exportFormat ??
    "CSV",
  )
    .trim()
    .toUpperCase();

  const maxRowsValue = Number(
    payload.maxRows ?? 10_000,
  );

  const maxRows =
    Number.isFinite(maxRowsValue) &&
    maxRowsValue > 0
      ? Math.min(
          Math.trunc(maxRowsValue),
          100_000,
        )
      : 10_000;

  const filtersJsonValue =
    payload.filtersJson;

  const filtersJson =
    typeof filtersJsonValue === "string" &&
    filtersJsonValue.trim()
      ? filtersJsonValue
      : JSON.stringify(
          payload.filters ?? {},
        );

  const includeInternalNotes =
    payload.includeInternalNotes === true;

  if (!reportCode) {
    throw new Error(
      "Report code is required.",
    );
  }

  if (
    !["CSV", "XLSX", "JSON"].includes(
      format,
    )
  ) {
    throw new Error(
      "Format must be CSV, XLSX or JSON.",
    );
  }

  return apiPost<ReportExportJobDto>(
    "/report-exports",
    {
      tenantId,
      reportCode,
      format,
      filtersJson,
      maxRows,
      includeInternalNotes,
    },
    {
      signal,
      idempotencyKey:
        createIdempotencyKey(
          "report-export-history",
        ),
    },
  );
},
  retryReportExport: (
    exportId: string,
    signal?: AbortSignal,
  ): Promise<ReportExportJobDto> => {
    const tenantId = requireTenantId();
    const normalizedExportId =
      requireExportId(exportId);

    const route =
      `/report-exports/${encodeURIComponent(
        normalizedExportId,
      )}/retry` +
      `?tenantId=${encodeURIComponent(
        tenantId,
      )}`;

    return apiPost<ReportExportJobDto>(
      route,
      {},
      { signal },
    );
  },

  cancelReportExport: (
    exportId: string,
    signal?: AbortSignal,
  ): Promise<ReportExportJobDto> => {
    const tenantId = requireTenantId();
    const normalizedExportId =
      requireExportId(exportId);

    const route =
      `/report-exports/${encodeURIComponent(
        normalizedExportId,
      )}/cancel` +
      `?tenantId=${encodeURIComponent(
        tenantId,
      )}`;

    return apiPost<ReportExportJobDto>(
      route,
      {},
      { signal },
    );
  },

downloadReportExportBlob: async (
  exportId: string,
  signal?: AbortSignal,
  exportFormat = "",
  fileName?: string,
): Promise<ReportBlobDownload> => {
  const tenantId = requireTenantId();

  const normalizedExportId =
    requireExportId(exportId);

  const blob = await apiDownload(
    `/report-exports/typed/${encodeURIComponent(
      normalizedExportId,
    )}/download`,
    {
      signal,
      query: {
        tenantId,
      },
      timeoutMs: 120_000,
    },
  );

  const extension =
    extensionFromFormat(exportFormat);

  const fallbackFilename =
    `cmts-report-${normalizedExportId}.${extension}`;

  return {
    blob,
    filename: safeReportFilename(
      fileName,
      fallbackFilename,
    ),
  };
},

  /*
   * Compatibility download method.
   * This now uses the real report-exports
   * controller route. It does not use /typed.
   */

  downloadExport: (
    exportId: string,
    signal?: AbortSignal,
  ): Promise<Blob> => {
    const tenantId = requireTenantId();
    const normalizedExportId =
      requireExportId(exportId);

    return apiDownload(
      `/report-exports/${encodeURIComponent(
        normalizedExportId,
      )}/download`,
      {
        signal,
        query: {
          tenantId,
        },
        timeoutMs: 120_000,
      },
    );
  },
};